import {
  Alert,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  HairlineCard,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CollectionPath, FileDetail, Step8Data } from '@munifocus/contracts-gov';
import { EntryWarrantPanel } from '../../components/EntryWarrantPanel';
import { EscalationStepLayout } from '../../components/EscalationStepLayout';
import { CouncilAuthorizationSection } from '../../components/clerk/ClerkApprovalsPanel';
import { EmergencyBypassPanel } from '../../components/EmergencyBypassPanel';
import { GeneratedDocuments } from '../../components/GeneratedDocuments';
import { CostRecoverySummaryCard } from '../../components/cost-recovery/CostRecoverySummaryCard';
import { useSaveStage } from '../../lib/files-api';
import { useForms, useGenerateDischargeCertificate } from '../../lib/forms-api';

function PreviewRow({ label, value }: { label: string; value: string | null }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {value && value.trim() !== '' ? value : '—'}
      </Typography>
    </Stack>
  );
}

/**
 * Type 3 sub-flow, Step 8 — Cleanup, repair & collection (ticket E5). The
 * municipality documents the work and recovers cost via municipal collection
 * (s. 143) or provincial reimbursement. Once compliance is met and the debt is
 * settled, the Form 5 Discharge Certificate is issued (preview here; PDF via
 * Epic G) and the file closes. Owner access refusal pauses for an entry warrant.
 */
export function Step8Cleanup({ file }: { file: FileDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'step_8');
  const forms = useForms(file.id);
  const generate = useGenerateDischargeCertificate(file.id);
  const initial = (file.stageData?.step_8 ?? {}) as Step8Data;

  const [workDescription, setWorkDescription] = useState(initial.workDescription ?? '');
  const [workBy, setWorkBy] = useState(initial.workBy ?? '');
  const [workCost, setWorkCost] = useState(initial.workCost ?? '');
  const [collectionPath, setCollectionPath] = useState<CollectionPath | ''>(
    initial.collectionPath ?? '',
  );
  const [accessRefused, setAccessRefused] = useState(initial.accessRefused ?? false);
  const [complianceMet, setComplianceMet] = useState(initial.complianceMet ?? false);
  const [debtSettled, setDebtSettled] = useState(initial.debtSettled ?? false);
  const [dischargeIssued, setDischargeIssued] = useState(initial.dischargeIssued ?? false);
  const [dischargeDate, setDischargeDate] = useState(initial.dischargeDate?.slice(0, 10) ?? '');

  // The Form 5 discharge is only available once the work is complied with and
  // the debt is settled (s. 143). Issuing it closes the file.
  const canDischarge = complianceMet && debtSettled;
  const issuing = canDischarge && dischargeIssued;

  const onSave = () =>
    save.mutate(
      {
        workDescription: workDescription.trim() || undefined,
        workBy: workBy.trim() || undefined,
        workCost: workCost.trim() || undefined,
        collectionPath: collectionPath || undefined,
        accessRefused,
        complianceMet,
        debtSettled,
        dischargeIssued: issuing,
        dischargeDate: issuing ? dischargeDate || undefined : undefined,
        // Issuing the Form 5 discharge is terminal — advance escalation -> closed.
        advance: issuing,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  // Assemble the Form 5 once compliance + debt are confirmed. The basis falls
  // back to a standard s. 143 closure statement.
  const onGenerate = () =>
    generate.mutate({
      closureReason: workDescription.trim() || t('gov.escalation.step8.dischargeBasisDefault'),
    });

  const dischargeForms = (forms.data ?? []).filter((f) => f.kind === 'discharge-certificate');

  return (
    <EscalationStepLayout
      file={file}
      step="step_8"
      onSave={onSave}
      saving={save.isPending}
      saveLabel={issuing ? t('gov.escalation.step8.issueDischarge') : t('common.actions.save')}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      {/* Second of the bypass's two entry points. A file can reach Step 8
          directly under s. 144(9) where work is needed to remove the danger
          before any notice period could run — and can be on the Step 5 route at
          the same time, which is why each step declares its own. */}
      <EmergencyBypassPanel fileId={file.id} enteredFrom="step_8" />

      {/* Remedial work is council's to authorize (s. 137): without an
          authorization the Clerk recorded, or an emergency bypass, the work
          below is refused on save. */}
      <CouncilAuthorizationSection fileId={file.id} showGateWarning />

      {/* Where cost recovery is on, the work's costs, the statement and the
          collection live there rather than in the free-text cost below. */}
      <CostRecoverySummaryCard fileId={file.id} />

      <Typography variant="subtitle2">{t('gov.escalation.step8.work')}</Typography>
      <TextField
        fullWidth
        multiline
        minRows={2}
        label={t('gov.escalation.step8.workDescription')}
        value={workDescription}
        onChange={(e) => setWorkDescription(e.target.value)}
      />
      <TextField
        fullWidth
        size="small"
        label={t('gov.escalation.step8.workBy')}
        value={workBy}
        onChange={(e) => setWorkBy(e.target.value)}
      />
      <TextField
        fullWidth
        size="small"
        label={t('gov.escalation.step8.workCost')}
        value={workCost}
        onChange={(e) => setWorkCost(e.target.value)}
      />

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.escalation.step8.collection')}</InputLabel>
        <Select
          label={t('gov.escalation.step8.collection')}
          aria-label={t('gov.escalation.step8.collection')}
          value={collectionPath}
          onChange={(e) => setCollectionPath(e.target.value as CollectionPath)}
        >
          <MenuItem value="municipal">{t('gov.escalation.step8.collectionMunicipal')}</MenuItem>
          <MenuItem value="provincial">{t('gov.escalation.step8.collectionProvincial')}</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox checked={accessRefused} onChange={(e) => setAccessRefused(e.target.checked)} />
        }
        label={t('gov.escalation.step8.accessRefused')}
      />
      {accessRefused && (
        <>
          <Alert severity="info">{t('gov.escalation.step8.accessRefusedNote')}</Alert>
          {/* Step 8 — access refused for the work; entry warrant required. */}
          <EntryWarrantPanel fileId={file.id} triggerPhase="step-8" />
        </>
      )}

      <Divider />

      <Typography variant="subtitle2">{t('gov.escalation.step8.discharge')}</Typography>
      <FormControlLabel
        control={
          <Checkbox checked={complianceMet} onChange={(e) => setComplianceMet(e.target.checked)} />
        }
        label={t('gov.escalation.step8.complianceMet')}
      />
      <FormControlLabel
        control={
          <Checkbox checked={debtSettled} onChange={(e) => setDebtSettled(e.target.checked)} />
        }
        label={t('gov.escalation.step8.debtSettled')}
      />

      {canDischarge && (
        <>
          {/* Read-only Form 5 preview — pre-populated from the file record. */}
          <HairlineCard sx={{ p: 2 }}>
            <Typography variant="overline">{t('gov.escalation.step8.formPreview')}</Typography>
            <Stack spacing={1} sx={{ mt: 1 }} divider={<Divider flexItem />}>
              <PreviewRow label={t('gov.escalation.step5.fileReference')} value={file.reference} />
              <PreviewRow label={t('gov.file.address')} value={file.address} />
              <PreviewRow
                label={t('gov.file.ownerName')}
                value={file.ownerUnknown ? t('gov.file.ownerUnknown') : file.ownerName}
              />
              <PreviewRow label={t('gov.escalation.step8.workCost')} value={workCost || null} />
            </Stack>
            <Stack sx={{ mt: 1.5 }}>
              <GeneratedDocuments
                forms={dischargeForms}
                onGenerate={onGenerate}
                generating={generate.isPending}
                isError={generate.isError}
                generateLabel={t('gov.escalation.step8.generateForm')}
              />
            </Stack>
          </HairlineCard>

          <FormControlLabel
            control={
              <Checkbox
                checked={dischargeIssued}
                onChange={(e) => setDischargeIssued(e.target.checked)}
              />
            }
            label={t('gov.escalation.step8.dischargeIssued')}
          />
          {dischargeIssued && (
            <>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('gov.escalation.step8.dischargeDate')}
                slotProps={{ inputLabel: { shrink: true } }}
                value={dischargeDate}
                onChange={(e) => setDischargeDate(e.target.value)}
              />
              <Alert severity="warning">{t('gov.escalation.step8.dischargeNote')}</Alert>
            </>
          )}
        </>
      )}
    </EscalationStepLayout>
  );
}
